import type { SchemaField } from '@cli-types/core-engine/schema.js'
import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships/is-user-relationship-target.js'
import { resolveDecimalPrecision } from '@core-engine/schema/type-mappers/column-defaults/resolve-decimal-precision.js'
import { resolveVarcharLength } from '@core-engine/schema/type-mappers/column-defaults/resolve-varchar-length.js'
import { assertSupportedSchemaFieldTree } from '@core-engine/schema/type-mappers/validation/assert-supported-schema-field-tree.js'
import { buildNestedListType } from './build-nested-list-type.js'

export function toDrizzleType(field: SchemaField, requiredImports: Set<string>): string {
  assertSupportedSchemaFieldTree(field)

  switch (field.type) {
    case 'uuid':
    case 'serial':
      requiredImports.add('uuid')
      return 'uuid().defaultRandom()'
    case 'string':
    case 'email':
    case 'varchar':
      requiredImports.add('varchar')
      return `varchar({ length: ${resolveVarcharLength(field)} })`
    case 'image':
    case 'video':
    case 'media':
      requiredImports.add('text')
      return 'text()'
    case 'text':
    case 'markdown':
    case 'richtext':
      requiredImports.add('text')
      return 'text()'
    case 'number':
      requiredImports.add('integer')
      return 'integer()'
    case 'decimal': {
      requiredImports.add('decimal')
      const { precision, scale } = resolveDecimalPrecision(field)
      return `decimal({ precision: ${precision}, scale: ${scale}, mode: 'number' })`
    }
    case 'boolean':
      requiredImports.add('boolean')
      return 'boolean()'
    case 'timestamp':
      requiredImports.add('timestamp')
      return "timestamp({ precision: 3, mode: 'string' })"
    case 'date':
      requiredImports.add('date')
      return "date({ mode: 'string' })"
    case 'time':
    case 'icon':
      requiredImports.add('varchar')
      return `varchar({ length: ${resolveVarcharLength(field)} })`
    case 'select':
      if (field.multiple) {
        requiredImports.add('jsonb')
        return 'jsonb().$type<string[]>()'
      }
      requiredImports.add('varchar')
      return `varchar({ length: ${resolveVarcharLength(field)} })`
    case 'gallery':
      requiredImports.add('jsonb')
      return 'jsonb().$type<string[]>()'
    case 'list':
      requiredImports.add('jsonb')
      if (field.fields && field.fields.length > 0) {
        const nestedType = buildNestedListType(field.fields)
        return `jsonb().$type<Array<{ ${nestedType} }>>()`
      }
      return 'jsonb().$type<string[]>()'
    case 'relationship':
      if (isUserRelationshipTarget(field.relationship)) {
        requiredImports.add('text')
        return 'text()'
      }
      requiredImports.add('uuid')
      return 'uuid()'
    default:
      requiredImports.add('text')
      return 'text()'
  }
}
